import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronRight, Reply, Send, Loader2, X } from "lucide-react";

export function SmsReplyItem({ it, i }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/communications/conversations/${it.conversation_id}/messages`, {
        body: text.trim(), client_key: `dash-${it.conversation_id}-${Date.now()}`,
      });
      if (r.data?.dispatched === false) toast.message(/gated off/i.test(r.data.note || "") ? "Reply saved — live SMS sending is currently switched off" : (r.data.note || r.data.error || "Reply saved but not sent"));
      else toast.success("Reply sent");
      setSent(true); setOpen(false); setText("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reply"); }
    finally { setSending(false); }
  };

  return (
    <div className="py-2" data-testid={`widget-unread-sms-row-${i}`}>
      <div className="flex items-start gap-2">
        <Link to={it.link || "/communications/inbox"} data-testid={`widget-unread-sms-item-${i}`}
          className="min-w-0 flex-1 -mx-2 px-2 rounded-lg hover:bg-slate-50 transition-colors">
          <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
          <div className="text-[11px] text-slate-500 truncate">{it.meta}</div>
          {it.job_id && <div className="text-[10px] text-slate-400 truncate">Job {it.job_id}</div>}
        </Link>
        {sent ? <span className="text-[11px] font-medium text-emerald-600 mt-1 shrink-0" data-testid={`widget-unread-sms-sent-${i}`}>Replied</span>
          : can("communications.send") && it.conversation_id && !open && (
          <button onClick={() => setOpen(true)} data-testid={`widget-unread-sms-reply-${i}`} title="Quick reply"
            className="mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50 shrink-0">
            <Reply className="w-3.5 h-3.5" /> Reply
          </button>
        )}
        {!open && !sent && <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-1.5" />}
      </div>
      {open && (
        <div className="mt-2 flex items-end gap-2" data-testid={`widget-unread-sms-reply-box-${i}`}>
          <textarea autoFocus rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={640}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Reply to ${it.title}…`} data-testid={`widget-unread-sms-reply-input-${i}`}
            className="flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200" />
          <div className="flex flex-col gap-1">
            <button onClick={send} disabled={sending || !text.trim()} data-testid={`widget-unread-sms-reply-send-${i}`} title="Send"
              className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center disabled:opacity-40 hover:bg-indigo-700">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
            <button onClick={() => { setOpen(false); setText(""); }} data-testid={`widget-unread-sms-reply-cancel-${i}`} title="Cancel"
              className="w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
